// src/diagnostics/serialLink.js
// Collegamento alla scheda con la Web Serial API (Chrome, Edge, Opera su
// computer, solo in HTTPS). Apre la porta, riavvia la scheda con DTR/RTS,
// legge il testo e lo passa a onData. Se la porta sparisce (cavo staccato, o
// la scheda si ricollega durante il reset) prova a riaprirla da sola.

const USB_CHIPS = {
  0x10c4: 'Silicon Labs CP210x',
  0x1a86: 'WCH CH340',
  0x0403: 'FTDI',
  0x303a: 'ESP32-S3 USB',
  0x067b: 'Prolific',
};

const sleep = (ms) => new Promise(r => setTimeout(r, ms));
const hex4 = (v) => v.toString(16).toUpperCase().padStart(4, '0');

/** 'ok' | 'unsupported' (browser senza Web Serial) | 'insecure' (pagina non HTTPS). */
export function serialSupport() {
  if (typeof navigator === 'undefined' || !('serial' in navigator)) return 'unsupported';
  if (typeof window !== 'undefined' && !window.isSecureContext) return 'insecure';
  return 'ok';
}

/** Descrizione della porta per il cliente e per il log. */
export function describePort(port) {
  let info = {};
  try { info = port?.getInfo?.() || {}; } catch { /* nessuna informazione */ }
  const vid = info.usbVendorId;
  const pid = info.usbProductId;
  if (vid == null) return { vid: null, pid: null, chip: null, label: 'porta seriale' };
  const chip = USB_CHIPS[vid] || null;
  return { vid, pid, chip, label: `${chip || 'USB'} (${hex4(vid)}:${hex4(pid ?? 0)})` };
}

/** Errore della Web Serial → { code, msg } in italiano. */
export function serialError(e, during) {
  const name = e?.name || '';
  if (name === 'NotFoundError') return { code: 'cancel', msg: 'Nessuna porta scelta. Se la scheda non compare nell\'elenco, prova un altro cavo USB (alcuni cavi caricano soltanto) o un\'altra presa.' };
  if (name === 'SecurityError') return { code: 'denied', msg: 'Il browser non ha concesso l\'accesso alla porta seriale.' };
  if (during === 'open' && (name === 'NetworkError' || name === 'InvalidStateError')) {
    return { code: 'busy', msg: 'La porta è già usata da un altro programma. Chiudi Arduino IDE, i monitor seriali e le altre schede del browser aperte su questa pagina, poi riprova.' };
  }
  if (during === 'open') return { code: 'open', msg: `Impossibile aprire la porta (${e?.message || name || 'errore sconosciuto'}). Scollega e ricollega la scheda, poi riprova.` };
  return { code: 'error', msg: e?.message || 'Errore della porta seriale' };
}

export class SerialLink {
  /**
   * onData(text)    testo ricevuto (già decodificato UTF-8)
   * onStatus(s)     { kind: 'lost' } porta sparita, si prova a riaprirla
   *                 { kind: 'back' } riaperta
   *                 { kind: 'gone' } non è tornata: test da interrompere
   */
  constructor({ onData, onStatus, options, reconnectMs = 8000 }) {
    this.onData = onData;
    this.onStatus = onStatus || (() => {});
    this.options = options;
    this.reconnectMs = reconnectMs;
    this.port = null;
    this.info = null;
    this.reader = null;
    this.keep = false;
    this.loop = null;
    this.reconnecting = false;
    this._onDisconnect = (ev) => {
      if (this.keep && ev?.target === this.port && !this.reconnecting) this._recover();
    };
  }

  async open(port) {
    this.port = port;
    this.info = describePort(port);
    await port.open(this.options);
    this.keep = true;
    navigator.serial.addEventListener('disconnect', this._onDisconnect);
    this.loop = this._read(port);
  }

  /**
   * Riavvio della scheda (auto-reset dell'ESP32: RTS → EN, DTR → GPIO0),
   * avvio normale: EN basso per un attimo con GPIO0 libero. Restituisce false
   * se la porta non gestisce i segnali.
   */
  async reset(pulseMs = 100) {
    const port = this.port;
    if (!port) return false;
    try {
      await port.setSignals({ dataTerminalReady: false, requestToSend: true });
      await sleep(pulseMs);
      return true;
    } catch {
      return false;
    } finally {
      // EN di nuovo alto anche se qualcosa va storto: la scheda non resta mai in reset
      try { await port.setSignals({ dataTerminalReady: false, requestToSend: false }); } catch { /* porta già chiusa */ }
    }
  }

  async _read(port) {
    while (this.keep && port === this.port && port.readable) {
      const decoder = new TextDecoderStream();
      const pipe = port.readable.pipeTo(decoder.writable).catch(() => {});
      const reader = decoder.readable.getReader();
      this.reader = reader;
      try {
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          if (value) this.onData(value);
        }
      } catch {
        // errore non fatale (dati rovinati, buffer pieno): si riprende a leggere;
        // se la porta è sparita, port.readable è null e si esce
      } finally {
        try { reader.releaseLock(); } catch { /* già rilasciato */ }
      }
      await pipe;
    }
    if (this.reader && port === this.port) this.reader = null;
    if (this.keep && port === this.port && !this.reconnecting) this._recover();
  }

  /** La porta è sparita: prova a riaprirla (stessa scheda, stesso chip USB). */
  async _recover() {
    this.reconnecting = true;
    this.onStatus({ kind: 'lost' });
    const old = this.port;
    try { await old?.close(); } catch { /* già chiusa */ }
    const until = Date.now() + this.reconnectMs;
    while (this.keep && Date.now() < until) {
      await sleep(400);
      let ports = [];
      try { ports = await navigator.serial.getPorts(); } catch { /* nessuna */ }
      const same = ports.filter(p => {
        const d = describePort(p);
        return d.vid === this.info?.vid && d.pid === this.info?.pid;
      });
      // prima la stessa porta, poi le altre dello stesso tipo
      same.sort((a, b) => (a === old ? -1 : b === old ? 1 : 0));
      for (const p of same) {
        if (!this.keep) break;
        try {
          await p.open(this.options);
          this.port = p;
          this.reconnecting = false;
          this.onStatus({ kind: 'back' });
          this.loop = this._read(p);
          return;
        } catch { /* non ancora pronta */ }
      }
    }
    this.reconnecting = false;
    if (this.keep) {
      this.keep = false;
      this.onStatus({ kind: 'gone' });
    }
  }

  /** Chiude tutto e libera la porta per altri programmi. */
  async close() {
    this.keep = false;
    try { navigator.serial.removeEventListener('disconnect', this._onDisconnect); } catch { /* noop */ }
    const r = this.reader;
    if (r) { try { await r.cancel(); } catch { /* già chiuso */ } }
    try { await this.loop; } catch { /* noop */ }
    try { await this.port?.close(); } catch { /* già chiusa */ }
    this.reader = null;
  }
}
